<template>
  <InputText
    v-if="field.control === 'text'"
    v-model="localValue"
    :placeholder="field.placeholder"
    :class="['w-auto max-w-full min-w-20', fieldError ? 'border-red-500!' : '']"
    style="field-sizing: content"
    v-tooltip="{ value: fieldError, disabled: !fieldError, showDelay: 300, class: 'text-sm' }"
    @update:modelValue="handleInput"
    @blur="handleBlur" />
  <InputNumber
    v-else-if="field.control === 'number'"
    v-model="localValue"
    :placeholder="field.placeholder"
    :class="[field.inputClass || 'w-[50px]', fieldError ? 'border-red-500!' : '']"
    v-tooltip="{ value: fieldError, disabled: !fieldError, showDelay: 300, class: 'text-sm' }"
    @update:modelValue="emit('input', field, $event)"
    @blur="handleBlur" />
</template>

<script setup>
import { useDebouncedField } from '@dashboard/composables/useDebouncedField'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import { ref } from 'vue'

const props = defineProps({
  condition: { type: Object, required: true },
  field: { type: Object, required: true },
  fieldError: { type: String, default: '' }
})

const emit = defineEmits(['input', 'blur'])

const lastAlertedFullUrl = ref(null)

const normalizeFieldValue = (value) => {
  if (typeof props.field.normalize === 'function') {
    return props.field.normalize(value, props.condition)
  }

  return { value, wasFullUrl: false }
}

const setConditionValue = (value) => {
  const normalized = normalizeFieldValue(value)
  props.condition[props.field.name] = normalized.value

  if (localValue.value !== normalized.value) {
    Promise.resolve().then(() => {
      if (localValue.value !== normalized.value) {
        localValue.value = normalized.value
      }
    })
  }

  if (normalized.wasFullUrl && typeof value === 'string') {
    if (lastAlertedFullUrl.value !== value) {
      lastAlertedFullUrl.value = value
      alert(
        `Enter only a page path. Query parameters belong in a separate query-parameter rule. The value was converted to ${normalized.value}.`
      )
    }
    return
  }

  lastAlertedFullUrl.value = null
}

// Buffer keystrokes locally and flush to the config-backed condition object on a
// debounce. Without this, every keystroke mutates config.dimensions[...].signals
// directly, which triggers the config store's deep watcher -> debounced save ->
// DraftConfigUpdated broadcast -> refetch + three-way merge. Keystrokes during
// that round-trip can end up reverted or scrambled. See ENG-1006 (and the same
// class of fix in ENG-830 / ENG-966).
const localValue = useDebouncedField(() => props.condition[props.field.name], setConditionValue)

function handleInput(value) {
  emit('input', props.field, normalizeFieldValue(value).value)
}

function handleBlur() {
  const normalizedValue = normalizeFieldValue(localValue.value).value
  localValue.flushNow()
  emit('blur', props.field, normalizedValue)
}
</script>
